import React from 'react';
import { Box, Typography, Button, Paper, Collapse, Divider, Stack } from '@mui/material';
import { Error as ErrorIcon, ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';

const ERROR_MESSAGES = [
  'Well… that wasn\'t supposed to happen.',
  'Congratulations! You found a bug 🐛',
  'This page took an unexpected coffee break.',
  'Plot twist: the app disagrees.',
  'Error 500: We blame the intern.',
  'Well, this is awkward.',
  'That didn\'t go as planned.',
  'This trade didn\'t clear. We\'re investigating.',
  'Temporary disruption. Liquidity returning shortly.',
  'Our systems hit resistance. Retrying…',
  'That request didn\'t settle as expected.',
  'We\'ve triggered a circuit breaker. Stand by.',
  'Risk team approved. System did not.',
  'This request exceeded our risk tolerance.',
  'We don\'t like this position either.',
  'Houston, we have a problem',
  'Oops! Something went wrong'
];

const getRandomErrorMessage = () => {
  return ERROR_MESSAGES[Math.floor(Math.random() * ERROR_MESSAGES.length)];
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      expandedStack: false,
      isClearing: false,
      randomErrorMessage: getRandomErrorMessage()
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, randomErrorMessage: getRandomErrorMessage() };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for debugging
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // Send to error monitoring service (Sentry, Rollbar, etc)
    this.reportError(error, errorInfo);
  }

  reportError = (error, errorInfo) => {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  };

  parseError = (error) => {
    if (!error) return { message: 'Unknown error', criticalFrame: null, allFrames: [] };

    const message = error.toString();
    const stack = error.stack ? error.stack.split('\n') : [];

    const frames = stack.slice(1).map((line, idx) => {
      const trimmed = line.trim();
      const match = trimmed.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)|at\s+(.+)/);
      if (match) {
        return {
          index: idx,
          method: match[1] || match[5] || 'anonymous',
          file: match[2] || '',
          line: match[3] || '',
          column: match[4] || '',
          raw: trimmed
        };
      }
      return { index: idx, raw: trimmed };
    });

    // First frame is the actual error location
    const criticalFrame = frames.length > 0 ? frames[0] : null;

    return { message, criticalFrame, allFrames: frames };
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearCacheAndReload = () => {
    this.setState({ isClearing: true });

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }

    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, expandedStack, isClearing } = this.state;
      const { message, criticalFrame, allFrames } = this.parseError(error);

      // Check if custom fallback is provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // minHeight: this.props.fullPage ? '100vh' : '300px',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 700,
              width: '100%',
              borderLeft: '4px solid',
              borderLeftColor: 'error.main'
            }}
          >
            {/* Error Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <ErrorIcon
                sx={{
                  fontSize: 40,
                  color: 'error.main',
                  mt: 0.5,
                  flexShrink: 0
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.dark', mb: 1 }}>
                  {this.props.errorTitle || this.state.randomErrorMessage}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    mb: 2
                  }}
                >
                  {this.props.errorMessage || message}
                </Typography>
              </Box>
            </Box>

            {/* Critical Stack Frame (Always Visible) */}
            {criticalFrame && (
              <>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#fff3e0',
                    border: '1px solid #ffb74d',
                    borderRadius: 1,
                    mb: 2
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.5 }}>
                    🔴 Error Location:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: 'text.primary'
                    }}
                  >
                    <strong>{criticalFrame.method}</strong>
                    {criticalFrame.file && ` at ${criticalFrame.file}`}
                    {criticalFrame.line && `:${criticalFrame.line}`}
                    {criticalFrame.column && `:${criticalFrame.column}`}
                  </Typography>
                </Box>
              </>
            )}

            {/* Expandable Stack Trace */}
            {allFrames && allFrames.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Button
                  size="small"
                  onClick={() => this.setState({ expandedStack: !expandedStack })}
                  endIcon={<ExpandMoreIcon sx={{ transform: expandedStack ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />}
                  sx={{ textTransform: 'none', p: 0 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {expandedStack ? 'Hide' : 'Show'} Full Stack Trace ({allFrames.length} frames)
                  </Typography>
                </Button>

                <Collapse in={expandedStack}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: '400px',
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }}
                  >
                    {allFrames.map((frame, idx) => (
                      <Box key={idx} sx={{ mb: 1, pb: 1, borderBottom: idx < allFrames.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: idx === 0 ? 'error.main' : 'text.secondary',
                            fontWeight: idx === 0 ? 600 : 400
                          }}
                        >
                          {idx + 1}. {frame.method && <strong>{frame.method}</strong>}
                          {frame.file && ` at ${frame.file}`}
                          {frame.line && `:${frame.line}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Development Details */}
            {import.meta.env.MODE === 'develospment' && errorInfo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    📋 Component Stack (Development Only):
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        whiteSpace: 'pre-wrap',
                        color: 'text.secondary'
                      }}
                    >
                      {errorInfo.componentStack}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Action Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReload}
                startIcon={<RefreshIcon />}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                Reload Page
              </Button>

              <Button
                variant="outlined"
                color="warning"
                onClick={this.handleClearCacheAndReload}
                disabled={isClearing}
                startIcon={<DeleteSweepIcon />}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {isClearing ? 'Clearing...' : 'Clear Cache & Reload'}
              </Button>
            </Stack>

            {/* Help Text */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 2,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              If the problem persists, try clearing your browser cache or creating an IT ticket.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
